import { useCallback } from 'react';
import { Panel } from '@/components/Panel';
import { CanvasChart } from '@/components/CanvasChart';
import { useLatency } from '@/hooks/useLatency';
import { Spinner, EmptyState } from '@00start/ui';
import { LINE_COLORS, SIGNAL, drawGridLines, formatTime } from '@/utils/chart-helpers';

const PAD = { top: 12, right: 16, bottom: 28, left: 48 };

function cleanServiceName(key: string): string {
  return key.replace(/riskscan-|-production|-staging/g, '');
}

export function LatencyChart() {
  const { data, loading } = useLatency();

  const services = data?.services ? Object.entries(data.services) : [];

  const onDraw = useCallback(
    (ctx: CanvasRenderingContext2D, width: number, height: number) => {
      if (!data || services.length === 0) return;

      const plotW = width - PAD.left - PAD.right;
      const plotH = height - PAD.top - PAD.bottom;

      // Global max across all services
      let maxVal = 10;
      for (const [, svc] of services) {
        for (const pt of svc.history) {
          if (pt.latencyMs > maxVal) maxVal = pt.latencyMs;
        }
      }

      drawGridLines(ctx, width, height, PAD, 0, maxVal, 5);

      // X axis time labels from first service's history (~every 6th point)
      const firstHistory = services[0][1].history;
      if (firstHistory.length > 0) {
        ctx.font = '10px SF Mono, monospace';
        ctx.fillStyle = 'rgba(148, 163, 184, 0.5)';
        ctx.textAlign = 'center';
        const step = Math.max(1, Math.floor(firstHistory.length / 6));
        for (let i = 0; i < firstHistory.length; i += step) {
          const x = PAD.left + (i / (firstHistory.length - 1)) * plotW;
          const y = height - PAD.bottom + 14;
          ctx.fillText(formatTime(firstHistory[i].timestamp), x, y);
        }
      }

      // Draw each service line
      for (let si = 0; si < services.length; si++) {
        const [, svc] = services[si];
        const color = LINE_COLORS[si % LINE_COLORS.length];
        const pts = svc.history;
        if (pts.length === 0) continue;

        ctx.beginPath();
        ctx.strokeStyle = color;
        ctx.lineWidth = 1.5;
        ctx.lineJoin = 'round';

        for (let i = 0; i < pts.length; i++) {
          const x = PAD.left + (i / Math.max(pts.length - 1, 1)) * plotW;
          const y = PAD.top + plotH - (pts[i].latencyMs / maxVal) * plotH;
          if (i === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.stroke();

        // Spike dots for latencyMs > 1000
        for (let i = 0; i < pts.length; i++) {
          if (pts[i].latencyMs > 1000) {
            const x = PAD.left + (i / Math.max(pts.length - 1, 1)) * plotW;
            const y = PAD.top + plotH - (pts[i].latencyMs / maxVal) * plotH;
            ctx.beginPath();
            ctx.arc(x, y, 3, 0, Math.PI * 2);
            ctx.fillStyle = SIGNAL.critical;
            ctx.fill();
          }
        }
      }
    },
    [data, services],
  );

  return (
    <Panel title="Latency Chart" wide mobilePriority={7}>
      {loading ? (
        <div className="flex justify-center py-4">
          <Spinner size="sm" />
        </div>
      ) : services.length === 0 ? (
        <EmptyState title="No latency data" description="No latency history available" />
      ) : (
        <>
          <CanvasChart onDraw={onDraw} height={200} />
          {/* Legend */}
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
            {services.map(([key], si) => (
              <div key={key} className="flex items-center gap-1.5">
                <span
                  style={{
                    display: 'inline-block',
                    width: 10,
                    height: 2,
                    backgroundColor: LINE_COLORS[si % LINE_COLORS.length],
                    borderRadius: 1,
                    flexShrink: 0,
                  }}
                />
                <span
                  className="text-[10px] text-slate-400"
                  style={{ fontFamily: 'SF Mono, monospace' }}
                >
                  {cleanServiceName(key)}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </Panel>
  );
}
